/**
 * The grid of the launcher tiles over the empty stage. The places go most important first, and
 * the column of a tile sets its size: 3x3 in the first column, 2x2 in the second, single units
 * after that. Six rows hold each size exactly. When the box is too narrow, the sizes step down.
 * When single units cannot hold all places, the launcher shows the most important ones.
 */

import type { LauncherPlaceKey, LauncherSlot } from '@/types/panels';
import { LauncherTileSize } from '@/types/panels';

/** The smallest grid unit a single tile still reads in: an icon, a title and a line under it. */
export const UNIT_MIN_WIDTH_PX = 136;
export const UNIT_MIN_HEIGHT_PX = 104;
/** Between the tiles. */
export const TILE_GAP_PX = 12;

/** The least common multiple of the tile sizes: each column of tiles fills it exactly. */
const ROWS_PER_BLOCK = 6;

/**
 * The tile sizes of the columns from the left; the last size repeats for every column after.
 * The first that holds every place in the box wins.
 */
const SIZE_STEPS: LauncherTileSize[][] = [
    [LauncherTileSize.Large, LauncherTileSize.Medium, LauncherTileSize.Small],
    [LauncherTileSize.Medium, LauncherTileSize.Small],
    [LauncherTileSize.Small],
];

/** What an operator comes to the app to do: watch, and look through what the cameras saw. */
const FIRST_PLACES: LauncherPlaceKey[] = [
    'cameras_list:cameras', 'ai_search:ask', 'ai_search:objects', 'ai_search:events',
    'ai_search:video',
];

/** The fleet's own administration: read when something needs changing, not on the way in. */
const LAST_PLACES: LauncherPlaceKey[] = ['servers', 'audit_log', 'organization_settings'];

interface LauncherLayout {
    columns: number;
    rows: number;
    slots: LauncherSlot[];
}

const EMPTY: LauncherLayout = { columns: 0, rows: 0, slots: [] };

function getRank(id: LauncherPlaceKey): number {
    if (FIRST_PLACES.includes(id)) {
        return 0;
    }
    return LAST_PLACES.includes(id) ? 2 : 1;
}

/** The places most important first, each rank in the order it came in (the rail's). */
export function sortByImportance(ids: LauncherPlaceKey[]): LauncherPlaceKey[] {
    return [...ids].sort((firstLauncherPlaceKey, second) =>
        getRank(firstLauncherPlaceKey) - getRank(second));
}

/** How many units of `sizePx` fit across `availablePx`, each gap included but the last. */
function fits(availablePx: number, sizePx: number): number {
    return Math.floor((availablePx + TILE_GAP_PX) / (sizePx + TILE_GAP_PX));
}

function getColumnSize(sizes: LauncherTileSize[], column: number): LauncherTileSize {
    return sizes[Math.min(column, sizes.length - 1)] ?? LauncherTileSize.Small;
}

/**
 * The row spans of the tiles of one column, top to bottom. A column whose size does not divide
 * the rows gives the rest to its first tiles, so it still reaches the bottom of the grid.
 */
function getColumnSpans(size: LauncherTileSize, rows: number): number[] {
    const count = Math.floor(rows / size);
    const rest = rows - count * size;
    return Array.from({ length: count }, (_, index) => size + (index < rest ? 1 : 0));
}

/** The grid columns that `count` places take at these sizes, or null when a size is too tall. */
function getColumnCount(sizes: LauncherTileSize[], rows: number, count: number): number | null {
    let columns = 0;
    let left = count;
    for (let column = 0; left > 0; column++) {
        const size = getColumnSize(sizes, column);
        const perColumn = Math.floor(rows / size);
        if (perColumn === 0) {
            return null;
        }
        left -= perColumn;
        columns += size;
    }
    return columns;
}

/** The tiles of `ids` in columns of `sizes`, top to bottom and then left to right. */
function place(
    sizes: LauncherTileSize[], rows: number, ids: LauncherPlaceKey[], unitHeightPx: number,
): LauncherSlot[] {
    const slots: LauncherSlot[] = [];
    let gridColumn = 1;
    let nextValue = 0;
    for (let column = 0; nextValue < ids.length; column++) {
        const size = getColumnSize(sizes, column);
        let gridRow = 1;
        for (const span of getColumnSpans(size, rows)) {
            const id = ids[nextValue];
            if (id === undefined) {
                break;
            }
            slots.push({
                id,
                column: gridColumn,
                row: gridRow,
                columnSpan: size,
                rowSpan: span,
                heightPx: span * unitHeightPx + (span - 1) * TILE_GAP_PX,
            });
            gridRow += span;
            nextValue++;
        }
        gridColumn += size;
    }
    return slots;
}

function getUnitHeight(heightPx: number, rows: number): number {
    return (heightPx - (rows - 1) * TILE_GAP_PX) / rows;
}

/** The row counts to try, fewest first: whole multiples of six, or all the box has. */
function getRowChoices(maxRows: number): number[] {
    if (maxRows < ROWS_PER_BLOCK) {
        return [maxRows];
    }
    return Array.from(
        { length: Math.floor(maxRows / ROWS_PER_BLOCK) },
        (_, index) => (index + 1) * ROWS_PER_BLOCK);
}

/** The tile's size: the width of the column it stands in. */
export function getTileSize(slot: LauncherSlot): LauncherTileSize {
    if (slot.columnSpan >= LauncherTileSize.Large) {
        return LauncherTileSize.Large;
    }
    return slot.columnSpan === LauncherTileSize.Medium
        ? LauncherTileSize.Medium
        : LauncherTileSize.Small;
}

/**
 * The heights a tile with a figure needs for a full description. Below them the figure keeps
 * its room and the description gives it up, since the figure is what changes between visits.
 */
const SMALL_ROOM_FOR_BOTH_PX = 124;
const MEDIUM_ROOM_FOR_BOTH_PX = 248;

/** How many lines of its description a tile has room for. */
export function getDescriptionLines(
    size: LauncherTileSize, heightPx: number, hasFigure: boolean,
): number {
    if (size === LauncherTileSize.Small) {
        return !hasFigure || heightPx >= SMALL_ROOM_FOR_BOTH_PX ? 1 : 0;
    }
    if (size === LauncherTileSize.Medium && hasFigure && heightPx < MEDIUM_ROOM_FOR_BOTH_PX) {
        return 1;
    }
    return 2;
}

/** The grid for `ids`, in rail order, over a box of this size. */
export function layoutLauncher(
    box: { width: number; height: number }, ids: LauncherPlaceKey[],
): LauncherLayout {
    // An unmeasured box would fit one tile of each place, so the launcher waits for the first
    // measurement.
    if (box.width <= 0 || box.height <= 0 || ids.length === 0) {
        return EMPTY;
    }
    const rankedLauncherPlaceKeys = sortByImportance(ids);
    const maxColumns = Math.max(1, fits(box.width, UNIT_MIN_WIDTH_PX));
    const maxRows = Math.max(1, fits(box.height, UNIT_MIN_HEIGHT_PX));
    for (const sizes of SIZE_STEPS) {
        for (const rows of getRowChoices(maxRows)) {
            const columns = getColumnCount(sizes, rows, rankedLauncherPlaceKeys.length);
            if (columns !== null && columns <= maxColumns) {
                const slots = place(sizes, rows, rankedLauncherPlaceKeys,
                    getUnitHeight(box.height, rows));
                return { columns, rows, slots };
            }
        }
    }
    // Too small for every place even in single units: the most important ones it holds.
    const shown = rankedLauncherPlaceKeys.slice(0, maxColumns * maxRows);
    const columns = Math.ceil(shown.length / maxRows);
    const slots = place(
        [LauncherTileSize.Small], maxRows, shown, getUnitHeight(box.height, maxRows));
    return { columns, rows: maxRows, slots };
}
